import {
  Account,
  BytesValue,
  ContractFunction,
  List,
  Struct,
  Transaction,
  U32Value,
  U64Value,
  VariadicValue,
} from "@multiversx/sdk-core";
import { BigNumber } from "bignumber.js";
import elrondHelper from "@/helpers/elrond";
import BLOCKCHAIN from "@/constants/blockchain";
import BaseContract from "@/contracts/baseContract";
import { TokenTransfer } from "@multiversx/sdk-core/out";

export interface MEME_VOTES {
  nonce: number;
  votes: number;
}

export const MEME_VOTING_CONSTANTS = {
  ERRORS: {
    THROTTLE: "Address already created a meme in the last 10 minutes",
    VOTES: "Not enough votes left currently",
    NOT_EXIST: "Meme does not exist",
  },
  THROTTLE_MEME_TIME: 600,
  VOTES_PER_ADDRESS_PER_PERIOD: 10,
};

export const MEME_VOTING_FUNCTIONS = {
  CREATE_MEME: "create_meme",
  VOTE_MEMES: "vote_memes",
};

class MemeVotingContract extends BaseContract {
  constructor() {
    super(BLOCKCHAIN.MEMES_VOTING_CONTRACT, "memes-voting", "MemesVoting");
  }

  // Endpoints
  async createMeme(
    name: string,
    url: string,
    category: string,
    signature: string,
  ): Promise<Transaction> {
    await this.getContractAbi();

    const interaction = this.contract.methods[MEME_VOTING_FUNCTIONS.CREATE_MEME]([
      name,
      url,
      category,
      BytesValue.fromHex(signature),
    ]).withGasLimit(
      50000000, // contains 25M gas from meme voting, 20M gas from meme auction + 5M just in case
    );

    interaction.withSingleESDTTransfer(TokenTransfer.fungibleFromBigInteger(BLOCKCHAIN.TOKEN_IDENTIFIER, BLOCKCHAIN.NUTS_PER_MEME));

    return interaction.buildTransaction();
  }

  async voteMemes(signature: string, nonces: number[], nutsValue: bigint): Promise<Transaction> {
    await this.getContractAbi();

    const interaction = this.contract.methods[MEME_VOTING_FUNCTIONS.VOTE_MEMES]([
      BytesValue.fromHex(signature),
      ...nonces,
    ]).withGasLimit(
      20000000, // 15M base, + 5M just in case
    );

    if (nutsValue > 0n) {
      interaction.withSingleESDTTransfer(TokenTransfer.fungibleFromBigInteger(BLOCKCHAIN.TOKEN_IDENTIFIER, nutsValue));
    }

    return interaction.buildTransaction();
  }

  // Views
  async currentPeriodLen(): Promise<number> {
    await this.getContractAbi();

    const query = await this.contract.createQuery({
      func: new ContractFunction("current_period_len"),
    });

    const result = await elrondHelper.cachedProxy.queryContract(query);

    const endpointDefinition = (await this.getContractAbi()).getEndpoint("current_period_len");
    const { firstValue } = this.resultParser.parseQueryResponse(result, endpointDefinition);

    return firstValue.valueOf().toNumber();
  }

  async currentPeriodMemesLatest(page: number): Promise<MEME_VOTES[]> {
    await this.getContractAbi();

    const query = await this.contract.createQuery({
      func: new ContractFunction("current_period_memes_latest"),
      args: [new U32Value(page)],
    });

    const result = await elrondHelper.cachedProxy.queryContract(query);

    const endpointDefinition = (await this.getContractAbi()).getEndpoint("current_period_memes_latest");
    const { firstValue, values } = this.resultParser.parseQueryResponse(result, endpointDefinition);

    return (firstValue as VariadicValue).getItems().map((result: Struct) => ({
      nonce: result.getFieldValue("field0").toNumber(),
      votes: result.getFieldValue("field1").toNumber(),
    }));
  }

  async periodLen(period: number): Promise<number> {
    await this.getContractAbi();

    const query = await this.contract.createQuery({
      func: new ContractFunction("period_len"),
      args: [new U64Value(new BigNumber(period))],
    });

    const result = await elrondHelper.cachedProxy.queryContract(query);

    const endpointDefinition = (await this.getContractAbi()).getEndpoint("period_len");
    const { firstValue } = this.resultParser.parseQueryResponse(result, endpointDefinition);

    return firstValue.valueOf().toNumber();
  }

  async periodMemesLatest(period: number, page: number): Promise<MEME_VOTES[]> {
    await this.getContractAbi();

    const query = await this.contract.createQuery({
      func: new ContractFunction("period_memes_latest"),
      args: [new U64Value(new BigNumber(period)), new U32Value(page)],
    });

    const result = await elrondHelper.cachedProxy.queryContract(query);

    const endpointDefinition = (await this.getContractAbi()).getEndpoint("period_memes_latest");
    const { firstValue } = this.resultParser.parseQueryResponse(result, endpointDefinition);

    return (firstValue as VariadicValue).getItems().map((result: Struct) => ({
      nonce: result.getFieldValue("field0").toNumber(),
      votes: result.getFieldValue("field1").toNumber(),
    }));
  }

  async periodTopMemes(period: number): Promise<MEME_VOTES[]> {
    await this.getContractAbi();

    const query = await this.contract.createQuery({
      func: new ContractFunction("period_top_memes"),
      args: [new U64Value(new BigNumber(period))],
    });

    const result = await elrondHelper.cachedProxy.queryContract(query);

    const endpointDefinition = (await this.getContractAbi()).getEndpoint("period_top_memes");
    const { firstValue } = this.resultParser.parseQueryResponse(result, endpointDefinition);

    return (firstValue as List).getItems().map((result: Struct) => ({
      nonce: result.getFieldValue("nft_nonce").toNumber(),
      votes: result.getFieldValue("votes").toNumber(),
    }));
  }
}

const memeVotingContract = new MemeVotingContract();

export default memeVotingContract;
